import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../lib/language";

const Block = ({ index, title, text, testid, shown }) => {
  const num = String(index + 1).padStart(2, "0");
  return (
    <article
      data-testid={testid}
      className={`relative ${shown ? "fade-up" : "opacity-0"}`}
      style={{ animationDelay: `${200 + index * 180}ms` }}
    >
      <div className="flex items-center gap-3">
        <span className="font-sans text-[10px] tracking-[0.4em] text-resilie-muted">
          / {num}
        </span>
        <span className="h-px w-10 bg-resilie-ink/20" aria-hidden="true" />
      </div>
      <h3 className="mt-6 font-serif text-3xl md:text-4xl font-light text-resilie-ink tracking-tight">
        {title}
      </h3>
      <p className="mt-5 font-sans text-[15px] leading-[1.8] text-resilie-muted max-w-sm">
        {text}
      </p>
    </article>
  );
};

const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current || shown) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [shown]);
  return [ref, shown];
};

export const Pillars = () => {
  const { t } = useLanguage();
  const [ref, shown] = useReveal();
  const items = [
    {
      title: t.pillars.mindful_title,
      text: t.pillars.mindful_text,
      testid: "pillar-mindful",
    },
    {
      title: t.pillars.intentional_title,
      text: t.pillars.intentional_text,
      testid: "pillar-intentional",
    },
    {
      title: t.pillars.elevated_title,
      text: t.pillars.elevated_text,
      testid: "pillar-elevated",
    },
  ];

  return (
    <section
      ref={ref}
      data-testid="pillars-section"
      className="bg-resilie-bg px-6 md:px-12 pb-28 md:pb-40"
    >
      <div className="max-w-6xl mx-auto">
        <p
          data-testid="pillars-kicker"
          className={`font-sans text-[10px] md:text-[11px] tracking-[0.5em] uppercase text-resilie-muted text-center ${
            shown ? "fade-up" : "opacity-0"
          }`}
        >
          — {t.pillars.kicker} —
        </p>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-y-16 md:gap-x-16 lg:gap-x-24">
          {items.map((item, i) => (
            <Block
              key={item.testid}
              index={i}
              title={item.title}
              text={item.text}
              testid={item.testid}
              shown={shown}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pillars;
